import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, MessageSquare, Camera, Ruler, FileText, Users, CheckCircle2, ArrowRight } from "lucide-react";
import PlatformFooter from "@/components/PlatformFooter";
import PlatformLogo from "@/components/PlatformLogo";
import LanguageSelector from "@/components/LanguageSelector";

const FEATURES = [
  { icon: MessageSquare, k: "f_wa" },
  { icon: Camera, k: "f_photo" },
  { icon: Ruler, k: "f_measure" },
  { icon: FileText, k: "f_quote" },
  { icon: Users, k: "f_team" },
  { icon: Sparkles, k: "f_ai" },
];

export default function Landing() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  useEffect(() => { http.get("/public/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {}); }, []);
  const annual = plans.find((p) => p.interval === "year");
  const monthly = plans.find((p) => p.interval === "month");
  const priceY = annual?.price_year ?? 600;
  const priceM = monthly?.price_month ?? 60;
  const saving = Math.max(0, priceM * 12 - priceY);
  const planFeatures = ["pf_customers", "pf_quotes", "pf_ai", "pf_wa", "pf_team"];

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#0B1120] text-slate-100" data-testid="landing-page">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 max-w-6xl mx-auto">
        <PlatformLogo size={36} dark testId="landing-platform-logo" className="min-w-0" />
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <LanguageSelector variant="dark" />
          <Button data-testid="landing-login-btn" variant="ghost" size="sm" className="text-slate-200 hover:bg-white/10 px-3" onClick={() => nav("/login")}>{t("landing.login")}</Button>
          <Button data-testid="landing-start-btn" size="sm" className="bg-sky-500 hover:bg-sky-400 text-[#0B1120] font-semibold px-3" onClick={() => nav("/register")}>{t("landing.start_trial")}</Button>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> {t("landing.trial_badge")}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold leading-tight break-words">{t("landing.hero_title")}</h1>
          <p className="text-slate-300 mt-5 text-lg">{t("landing.hero_sub")}</p>
          <p className="text-slate-400 mt-4 text-sm">{t("landing.trial_note")}</p>
          <div className="flex flex-col sm:flex-row gap-3 mt-7">
            <Button data-testid="hero-start-btn" size="lg" className="bg-sky-500 hover:bg-sky-400 text-[#0B1120] font-semibold" onClick={() => nav("/register")}>
              {t("landing.start_trial")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button data-testid="hero-login-btn" size="lg" variant="outline" className="border-white/20 text-slate-100 hover:bg-white/10 bg-transparent" onClick={() => nav("/login")}>{t("landing.login")}</Button>
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8 w-full" data-testid="landing-pricing">
          <div className="text-sm text-slate-400">{t("landing.pricing_title")}</div>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-sky-400/40 bg-sky-500/10 p-4 relative" data-testid="landing-plan-annual">
              <span className="absolute -top-2.5 left-4 text-[10px] font-bold uppercase tracking-wide bg-emerald-500 text-[#0B1120] px-2 py-0.5 rounded-full">{t("landing.recommended")}</span>
              <div className="font-heading font-extrabold mt-1">{t("landing.annual_name")}</div>
              <div className="text-3xl font-extrabold text-sky-300 mt-1 break-words">€{priceY}<span className="text-sm text-slate-400 font-normal">{t("landing.per_year")}</span></div>
              {saving > 0 && <div className="text-xs text-emerald-300 mt-1 font-semibold" data-testid="landing-annual-saving">{t("landing.save_badge", { amount: saving })}</div>}
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="landing-plan-monthly">
              <div className="font-heading font-extrabold mt-1">{t("landing.monthly_name")}</div>
              <div className="text-3xl font-extrabold text-slate-100 mt-1 break-words">€{priceM}<span className="text-sm text-slate-400 font-normal">{t("landing.per_month")}</span></div>
              <div className="text-xs text-slate-500 mt-1">€{priceM * 12}{t("landing.per_year")}</div>
            </div>
          </div>
          {saving > 0 && <div className="text-xs text-slate-400 mt-3" data-testid="landing-saving-note">{t("landing.save_annual", { amount: saving })}</div>}
          <div className="text-xs text-slate-500 mt-1">{t("landing.choose_later")}</div>
          <ul className="space-y-2 mt-5 text-sm text-slate-200">
            {planFeatures.map((f) => (
              <li key={f} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" /> {t(`landing.${f}`)}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map((f) => (
          <div key={f.k} className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <f.icon className="w-6 h-6 text-sky-300" />
            <div className="font-semibold mt-3">{t(`landing.${f.k}_t`)}</div>
            <div className="text-sm text-slate-400 mt-1">{t(`landing.${f.k}_d`)}</div>
          </div>
        ))}
      </section>

      <footer className="border-t border-white/10"><PlatformFooter dark /></footer>
    </div>
  );
}
